import { MutableRefObject, Ref, RefObject, version as reactVersion, useRef, useCallback, ForwardedRef } from 'react';

export function useLoadingAnimationCallbackRef(): (element: HTMLElement | null) => void {
  const animationRef = useRef<Animation | null>(null);
  return useCallback(element => {
    if (element && typeof element.animate === 'function') {
      // Use web animation API instead of CSS animations so that we can
      // synchronize it between all loading elements on the page (via startTime).
      animationRef.current = element.animate([{ backgroundPosition: '100%' }, { backgroundPosition: '0%' }], {
        duration: 2000,
        iterations: Infinity,
        easing: 'ease-in-out',
      });
      animationRef.current.startTime = 0;
    } else {
      animationRef.current?.cancel();
      animationRef.current = null;
    }
  }, []);
}

export function inertValue(value: boolean) {
  const pieces = reactVersion.split('.');
  const major = parseInt(pieces[0], 10);
  if (major >= 19) {
    return value;
  }
  // compatibility with React < 19
  return value ? 'true' : undefined;
}

function isMutableRef<T>(ref: RefObject<T> | MutableRefObject<T>): ref is MutableRefObject<T> {
  const descriptor = Object.getOwnPropertyDescriptor(ref, 'current');
  return !!descriptor && descriptor.writable === true;
}

export function useMergedRef<T>(...refs: Array<ForwardedRef<T> | Ref<T> | undefined>) {
  return useCallback(
    (node: T) => {
      refs.forEach(ref => {
        if (!ref) return;
        if ('current' in ref) {
          if (isMutableRef(ref)) {
            ref.current = node;
          }
        } else {
          ref(node);
        }
      });
    },
    [refs],
  );
}
